// src/Dashboard/pages/user/TechniquesQuizzDashboard/hooks/useTechniquesMemoryGame.js
// Adapte le chemin si besoin.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import {
  DEFAULT_MEMORY_UI_STATE,
  MEMORY_STORAGE_KEY,
  buildMemoryCardCountOptions,
  buildMemoryDeck,
  buildMemoryGradeOptions,
  filterTechniquesByMemoryGrades,
  getStoredMemoryState,
  normalizeMemoryGrades,
} from "../utils/techniquesQuizzUtils";

export function useTechniquesMemoryGame({ techniques = [], loading = false }) {
  const [ui, setUi] = useState(() => getStoredMemoryState());

  const [cards, setCards] = useState([]);
  const [flippedIds, setFlippedIds] = useState([]);
  const [matchedIds, setMatchedIds] = useState([]);
  const [moves, setMoves] = useState(0);
  const [locked, setLocked] = useState(false);
  const [finishedAt, setFinishedAt] = useState(null);

  const hideTimerRef = useRef(null);

  const persistUi = useCallback((nextUi) => {
    const safeUi = {
      ...DEFAULT_MEMORY_UI_STATE,
      ...nextUi,
      grades: normalizeMemoryGrades(nextUi.grades),
      cardCount: Number(nextUi.cardCount || DEFAULT_MEMORY_UI_STATE.cardCount),
    };

    setUi(safeUi);

    try {
      localStorage.setItem(MEMORY_STORAGE_KEY, JSON.stringify(safeUi));
    } catch {
      // localStorage indisponible
    }
  }, []);

  const updateUi = useCallback(
    (patch) => {
      persistUi({
        ...ui,
        ...patch,
      });
    },
    [persistUi, ui]
  );

  const gradeOptions = useMemo(
    () => buildMemoryGradeOptions(techniques),
    [techniques]
  );

  const availableTechniques = useMemo(
    () => filterTechniquesByMemoryGrades(techniques, ui.grades),
    [techniques, ui.grades]
  );

  const cardCountOptions = useMemo(
    () => buildMemoryCardCountOptions(techniques, ui.grades),
    [techniques, ui.grades]
  );

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const generateGame = useCallback(
    ({ silent = false } = {}) => {
      clearHideTimer();

      const options = buildMemoryCardCountOptions(techniques, ui.grades);

      if (options.length === 0) {
        setCards([]);
        setFlippedIds([]);
        setMatchedIds([]);
        setMoves(0);
        setLocked(false);
        setFinishedAt(null);

        if (!silent && !loading) {
          toast.info(
            "Pas assez de techniques pour générer un jeu des familles avec ces critères."
          );
        }

        return;
      }

      const wantedCount = options.includes(Number(ui.cardCount))
        ? Number(ui.cardCount)
        : options[options.length - 1];

      if (wantedCount !== Number(ui.cardCount)) {
        persistUi({
          ...ui,
          cardCount: wantedCount,
        });
        return;
      }

      const nextCards = buildMemoryDeck(techniques, {
        grades: ui.grades,
        cardCount: wantedCount,
      });

      setCards(nextCards);
      setFlippedIds([]);
      setMatchedIds([]);
      setMoves(0);
      setLocked(false);
      setFinishedAt(null);
    },
    [clearHideTimer, loading, persistUi, techniques, ui]
  );

  useEffect(() => {
    if (loading) return;
    generateGame({ silent: true });
  }, [loading, generateGame]);

  useEffect(() => {
    return () => {
      clearHideTimer();
    };
  }, [clearHideTimer]);

  const restart = useCallback(() => {
    generateGame();
  }, [generateGame]);

  const flipCard = useCallback(
    (card) => {
      if (!card || locked || finishedAt) return;

      const cardId = card.uid;

      if (flippedIds.includes(cardId) || matchedIds.includes(cardId)) {
        return;
      }

      const nextFlipped = [...flippedIds, cardId];

      if (nextFlipped.length > 2) return;

      setFlippedIds(nextFlipped);

      if (nextFlipped.length !== 2) return;

      const firstCard = cards.find((item) => item.uid === nextFlipped[0]);
      const secondCard = cards.find((item) => item.uid === nextFlipped[1]);

      if (!firstCard || !secondCard) return;

      setMoves((previous) => previous + 1);

      const isMatch =
        String(firstCard.techniqueId) === String(secondCard.techniqueId);

      if (isMatch) {
        const nextMatchedIds = [...matchedIds, firstCard.uid, secondCard.uid];

        setMatchedIds(nextMatchedIds);
        setFlippedIds([]);

        if (nextMatchedIds.length === cards.length) {
          setFinishedAt(Date.now());
        }

        return;
      }

      setLocked(true);

      hideTimerRef.current = setTimeout(() => {
        setFlippedIds([]);
        setLocked(false);
        hideTimerRef.current = null;
      }, 850);
    },
    [cards, finishedAt, flippedIds, locked, matchedIds]
  );

  const stats = useMemo(() => {
    const totalCards = cards.length;
    const totalPairs = totalCards / 2;
    const matchedCards = matchedIds.length;
    const matchedPairs = matchedCards / 2;
    const remainingPairs = Math.max(0, totalPairs - matchedPairs);
    const progress =
      totalCards > 0 ? Math.round((matchedCards / totalCards) * 100) : 0;

    return {
      totalCards,
      totalPairs,
      matchedCards,
      matchedPairs,
      remainingPairs,
      moves,
      progress,
      finished: Boolean(finishedAt),
    };
  }, [cards.length, finishedAt, matchedIds.length, moves]);

  return {
    ui,
    updateUi,

    cards,
    flippedIds,
    matchedIds,
    locked,
    finishedAt,

    gradeOptions,
    cardCountOptions,
    availableTechniques,

    stats,

    flipCard,
    restart,
  };
}

export default useTechniquesMemoryGame;